/**
 * @class BaseModal
 * @extends HTMLElement
 *
 * Base popup element.
 */

export class BaseModal extends HTMLElement {
  #originalParent = null;
  #nextSibling = null;
  #previouslyFocused = null;
  #triggerElement = null;

  /**
   * When true, the modal is appended to `<body>` on open to ensure
   * correct stacking context.
   *
   * @returns {boolean}
   */
  get shouldAppendToBody() {
    return this.hasAttribute('should-append-to-body');
  }

  /**
   * When true, the modal remains at `<body>` after first open.
   * Prevents media elements from reloading when moved in the DOM.
   *
   * @returns {boolean}
   */
  get persistAtBody() {
    return false;
  }

  get triggerElement() {
    return this.#triggerElement;
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    document.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeyDown);
  }

  async open() {
    if (this.isOpen) return;

    this.#previouslyFocused = document.activeElement;

    this.removeAttribute('inert');

    if (this.shouldAppendToBody && this.parentNode !== document.body) {
      if (!this.persistAtBody) {
        this.#originalParent = this.parentNode;
        this.#nextSibling = this.nextSibling;
      }

      document.body.appendChild(this);
      await new Promise((resolve) => requestAnimationFrame(resolve));
    }

    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';

    trapFocus(this);
    this.onOpen();
  }

  async close() {
    if (!this.isOpen) return;

    this.removeAttribute('open');
    document.body.style.overflow = '';

    removeTrapFocus(this);
    this.onClose();

    await this.#awaitTransition();

    this.setAttribute('inert', '');

    if (this.shouldAppendToBody && !this.persistAtBody) {
      this.#restorePosition();
    }

    this.#previouslyFocused?.focus();
    this.#previouslyFocused = null;
  }

  toggle(triggerElement = null) {
    this.#triggerElement = triggerElement;

    this.isOpen ? this.close() : this.open();
  }

  onOpen() {}
  onClose() {}

  #restorePosition() {
    if (!this.#originalParent) return;

    if (this.#nextSibling) {
      this.#originalParent.insertBefore(this, this.#nextSibling);
    } else {
      this.#originalParent.appendChild(this);
    }

    this.#originalParent = null;
    this.#nextSibling = null;
  }

  #awaitTransition() {
    return new Promise((resolve) => {
      this.addEventListener('transitionend', resolve, { once: true });
      setTimeout(resolve, 350);
    });
  }

  #onKeyDown = (event) => {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  };
}

if (!customElements.get("base-modal")) {
  customElements.define("base-modal", BaseModal);
}

/**
 * Global click handler to trigger modals based on aria-controls attributes.
 */
document.addEventListener ("DOMContentLoaded", () => {
  document.addEventListener("click", (event) => {
    const modalTrigger = event.target.closest("[aria-controls]");

    if (!modalTrigger) {
      return;
    }

    const modalId = modalTrigger.getAttribute("aria-controls");
    const modal = document.getElementById(modalId);

    if (!(modal instanceof BaseModal)) {
      return;
    }

    event.preventDefault();
    modal.toggle(modalTrigger);
  });
});
